import React, { useEffect, useRef, useState } from 'react';
import propTypes from 'prop-types';
import HedinIcon from '@hedinbil/hedin-ui/dist/icons/components/HedinIcon';
import ReactDOM from 'react-dom';
import FilterPage from './FilterPage';
import useBreakpoint from '@hedinbil/hedin-ui/dist/utils/useBreakpoint';
import { canHaveNestedPage, getFilterTypeName, getNestedSubtitle, getPageType } from './utils';
import {
  CLEAR_QUERY_PARAMS_URL,
  GET_QUERY_PARAMS,
  UPDATE_ALL_DYNAMIC_FILTERS,
  UPDATE_QUERY_PARAMS_URL,
} from '../../../../Feature/hedin-filters/actions/types';
import { getDynamicCarFilterItems } from '../../../../Feature/hedin-filters/helpers/getDynamicCarFilterItems';
import { setLoadingBar } from '../../RouteHandler';
import FacetList from './FacetList';
import { getDynamicFilterItems } from '../../../../Feature/hedin-filters/helpers/getDynamicFilterItems';
import { getItemsKey } from '../../../../Feature/hedin-filters/helpers/filterItems';

const Filter = (props) => {
  const {
    isOpen,
    onClose,
    facets,
    filterType,
    vehicleType,
    datasourceItems,
    onUpdateList,
    resetPageNumber,
    perPage,
    updateTotalCount,
    setFilterCount,
    predefinedQuery,
    combineQueries,
    currentSorting,
    currentQuery,
  } = props;
  const modalContentRef = useRef(null);
  const [pageIndex, setPageIndex] = useState(null);
  const [nestedPageIndex, setNestedPageIndex] = useState(null);
  const [customClass, setCustomClass] = useState('');
  const [loading, setLoading] = useState(false);
  const [animationInterval, setAnimationInterval] = useState(40);
  const [nestedPageTransitionBack, setNestedPageTransitionBack] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const breakpoint = useBreakpoint();

  useEffect(() => {
    if (breakpoint === 'lg' || breakpoint === 'xl') {
      setAnimationInterval(240);
    } else {
      setAnimationInterval(40);
    }
  }, [breakpoint]);

  useEffect(() => {
    if (isOpen) {
      document.querySelector('body').style.overflowY = 'hidden';
    }

    return () => {
      document.querySelector('body').style.overflowY = 'auto';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const onClickBackdrop = (e) => {
    // Check if clicked outside modal__content
    if (modalContentRef.current && !modalContentRef.current.contains(e.target)) {
      onShowItems();
      onClose();

      // If user hasn't changed items, don't fetch new filters.
      if (hasChanged) {
        updateDynamicItems();
      }
    }
  };

  const onCloseFilter = () => {
    if (nestedPageIndex === null) {
      setCustomClass('hb-filter-page-exit');
    }

    setTimeout(() => {
      if (nestedPageIndex !== null) {
        setNestedPageIndex(null);
        setPageIndex(null);
        setCustomClass('hb-filter-page-exit');

        setTimeout(() => {
          setCustomClass('');
          onClose();
        }, animationInterval);
      } else {
        setPageIndex(null);
        setCustomClass('');
        onClose();
      }
    }, 240); // Should always be the same here no matter breakpoint (slide out animation)
  };

  const onShowItems = (closeFunc) => {
    if (closeFunc) {
      closeFunc();
    } else {
      onCloseFilter();
    }

    // props.onFilter();
  };

  const onClearFilter = () => {
    props.filterActions(CLEAR_QUERY_PARAMS_URL, null, { filterType });
    props.onClear();
    setHasChanged(false);
    if (setFilterCount) setFilterCount(0);
  };

  const updateDynamicItems = () => {
    let queryParams = props.filterActions(GET_QUERY_PARAMS, null, { filterType });
    const selectedFacets = [];
    datasourceItems?.ProductListFilterOptions?.map((facet) =>
      selectedFacets.push(facet?.fields?.FilterOptionKey?.value)
    );

    let activeFiltersCount = 0;

    (facets || []).map((facetItem) => {
      activeFiltersCount += facetItem.activeFilters > 0 ? 1 : 0;
    });

    if (selectedFacets.length > 0) {
      queryParams += (queryParams.length > 0 ? '&' : '') + 'saf=' + selectedFacets.join(',');
    }
    setLoading(true);
    setLoadingBar(true);

    if (setFilterCount) setFilterCount(activeFiltersCount);
    props.filterActions(UPDATE_QUERY_PARAMS_URL, null, {
      key: currentSorting,
      parentValue: 0,
      filterType,
    });

    if (filterType === 'Car') {
      if (predefinedQuery && combineQueries) {
        queryParams = combineQueries(predefinedQuery, queryParams);
      }
      getDynamicCarFilterItems({
        vehicleType,
        queryParams,
        perPage,
        pageNumber: 0,
        argSorting: currentSorting,
        filterActions: props.filterActions,
        currentQuery,
        eventType: 'FILTER',
      }).then((res) => {
        if (!res) return;

        props.filterActions(UPDATE_ALL_DYNAMIC_FILTERS, res?.filter, { filterType });
        onUpdateList(res?.cars);
        onDynamicListUpdate(res);
      });
    } else {
      getDynamicFilterItems({
        filterType,
        queryParams,
        perPage,
        pageNumber: 0,
        filterActions: props.filterActions,
        currentQuery,
        eventType: 'FILTER',
      }).then((res) => {
        if (!res) return;

        props.filterActions(UPDATE_ALL_DYNAMIC_FILTERS, res.filter, {
          parentValue: queryParams,
          filterType,
        });
        onUpdateList(res[getItemsKey(filterType)]);
        onDynamicListUpdate(res);
      });
    }
  };

  const onDynamicListUpdate = (res) => {
    setLoading(false);
    setLoadingBar(false);
    setHasChanged(false);
    resetPageNumber();
    updateTotalCount(res?.totalCount);
  };

  const onChangeActivePage = (index) => {
    setPageIndex(index);

    // If user hasn't changed items, don't fetch new filters.
    if (hasChanged) {
      updateDynamicItems();
    }
  };

  const onBackFilterPage = (isNested) => {
    if (isNested) {
      setNestedPageIndex(null);
    } else {
      setPageIndex(null);
    }

    // If user hasn't changed items, don't fetch new filters.
    if (hasChanged) {
      updateDynamicItems();
    }
  };

  return ReactDOM.createPortal(
    <div className={`hb-modal ${isOpen ? 'hb-modal-open' : ''}`} onClick={onClickBackdrop}>
      <div className="page-container">
        <div className="d-flex h-100" ref={modalContentRef}>
          <div className={`hb-modal__content hb-filter hb-filter-page-first ${customClass}`}>
            <div className="hb-filter__header">
              <p className="hb-filter__header-title">
                ALLE FILTER
                {pageIndex === null && (
                  <button
                    className="button--clean hb-filter__header-cross"
                    onClick={() => onCloseFilter()}
                  >
                    <HedinIcon iconName="Cross" />
                  </button>
                )}
              </p>
            </div>

            <FacetList
              facets={facets}
              pageIndex={pageIndex}
              loading={loading}
              onChangeActivePage={onChangeActivePage}
            />

            <div className="hb-filter__footer">
              {pageIndex === null && (
                <>
                  <button
                    className="button button__primary hb-filter__footer-action-btn"
                    onClick={() => onShowItems()}
                  >
                    SE {getFilterTypeName(filterType)}
                  </button>
                  <button
                    className="button button--clean hb-filter__footer-clear-btn"
                    onClick={() => onClearFilter()}
                  >
                    <HedinIcon iconName="TrashCan" className="hb-filter__footer-clear-btn-icon" />
                    <span>FJERN</span>
                  </button>
                </>
              )}
            </div>
          </div>
          {pageIndex !== null && (
            <FilterPage
              onClose={() => onCloseFilter()}
              onBack={() => onBackFilterPage(false)}
              onNestedPage={(index) => setNestedPageIndex(index)}
              hasNestedPage={nestedPageIndex !== null}
              facet={facets[pageIndex]}
              nestedPageTransitionBack={nestedPageTransitionBack}
              animationInterval={animationInterval}
              title={facets[pageIndex].title}
              isNested={false}
              onShowItems={(func) => onShowItems(func)}
              onClearFilter={() => onClearFilter()}
              canHaveNestedPage={canHaveNestedPage(facets[pageIndex]?.key)}
              filterActions={props.filterActions}
              filterType={filterType}
              nestedPageIndex={nestedPageIndex}
              setHasChanged={setHasChanged}
              updateDynamicItems={updateDynamicItems}
            />
          )}
          {nestedPageIndex !== null && (
            <FilterPage
              onClose={() => onCloseFilter()}
              onBack={() => onBackFilterPage(true)}
              facet={facets[pageIndex].options[nestedPageIndex]}
              setNestedPageTransitionBack={(val) => setNestedPageTransitionBack(val)}
              animationInterval={animationInterval}
              title={getNestedSubtitle(facets[pageIndex]?.subTitle, facets[pageIndex]?.key)}
              onShowItems={(func) => onShowItems(func)}
              onClearFilter={() => onClearFilter()}
              isNested={true}
              filterActions={props.filterActions}
              filterType={filterType}
              nestedPageIndex={nestedPageIndex}
              setHasChanged={setHasChanged}
              updateDynamicItems={updateDynamicItems}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

Filter.propTypes = {
  text: propTypes.string,
  className: propTypes.string,
  onClick: propTypes.func,
};

export default Filter;
